import React, { useState, useEffect } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Plus, Music, Send, Play, Heart, Download, Trash2, ChevronRight,
  CheckCircle2, Clock, Users, X, ExternalLink, Search,
} from 'lucide-react';
import { promoAPI, contactsAPI } from '@/lib/api';
import { useProject } from '@/lib/ProjectContext';
import { toast } from 'sonner';
import { useForm } from 'react-hook-form';
import { safeFormat } from '@/lib/utils';

// ── Stat chip ─────────────────────────────────────────────────────────────────
const Stat = ({ icon: Icon, count, label, color }) => (
  <div className="flex items-center gap-1.5">
    <Icon className={`w-3.5 h-3.5 ${color}`} />
    <span className="text-sm font-semibold text-[#1E2A33]">{count}</span>
    <span className="text-xs text-[#5C6670]">{label}</span>
  </div>
);

// ── Campaign card ─────────────────────────────────────────────────────────────
const CampaignCard = ({ campaign, onClick, onDelete }) => {
  const stats = campaign.stats || {};
  return (
    <Card
      className="hover:border-[#A62B1F] hover:shadow-md transition-all cursor-pointer group"
      onClick={onClick}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            {campaign.artwork_url ? (
              <img
                src={campaign.artwork_url}
                alt="Artwork"
                className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-[#A62B1F] to-[#A62B1F] flex items-center justify-center flex-shrink-0">
                <Music className="w-6 h-6 text-white" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <p className="font-bold text-[#1E2A33] truncate" style={{ fontFamily: 'Manrope' }}>
                  {campaign.track_title}
                </p>
                <Badge
                  variant="outline"
                  className={campaign.status === 'sent'
                    ? 'text-xs border-green-300 bg-green-50 text-green-700'
                    : 'text-xs border-[#E3DDD1] text-[#5C6670]'}
                >
                  {campaign.status === 'sent' ? '✓ Sent' : 'Draft'}
                </Badge>
              </div>
              <p className="text-sm text-[#5C6670] truncate">{campaign.artist}</p>
              <p className="text-xs text-[#9AA3AB] mt-0.5">{campaign.title}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 hover:bg-red-50"
              onClick={(e) => { e.stopPropagation(); onDelete(campaign); }}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
            <ChevronRight className="w-4 h-4 text-[#CFC8BB] group-hover:text-[#A62B1F]" />
          </div>
        </div>

        {campaign.status === 'sent' && (
          <div className="mt-4 pt-3 border-t border-[#F3EFE7] flex items-center gap-4 flex-wrap">
            <Stat icon={Users} count={stats.total || 0} label="sent" color="text-[#5C6670]" />
            <Stat icon={Play} count={stats.played || 0} label="played" color="text-[#4C6B82]" />
            <Stat icon={Heart} count={stats.liked || 0} label="liked" color="text-[#A62B1F]" />
            <Stat icon={Download} count={stats.downloaded || 0} label="downloaded" color="text-[#A62B1F]" />
            {campaign.sent_at && (
              <span className="text-xs text-[#9AA3AB] ml-auto">
                {safeFormat(campaign.sent_at, 'MMM d, yyyy')}
              </span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── Contact selector ──────────────────────────────────────────────────────────
const ContactPicker = ({ selectedIds, onChange }) => {
  const { projects } = useProject();
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    contactsAPI.list({}).then(r => {
      setContacts(r.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const filtered = contacts.filter(c =>
    !search ||
    c.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase()) ||
    c.company?.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (id) => {
    onChange(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const selectAll = () => onChange(filtered.map(c => c._id));
  const clearAll = () => onChange([]);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label className="text-sm font-medium text-[#1E2A33]">
          Select Recipients ({selectedIds.length} selected)
        </Label>
        <div className="flex gap-2">
          <button onClick={selectAll} className="text-xs text-[#A62B1F] hover:underline">All</button>
          <button onClick={clearAll} className="text-xs text-[#5C6670] hover:underline">None</button>
        </div>
      </div>
      <div className="relative mb-2">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#5C6670]" />
        <Input
          placeholder="Search contacts…"
          className="pl-8 h-8 text-sm"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>
      <div className="border border-[#E3DDD1] rounded-lg max-h-52 overflow-y-auto">
        {loading ? (
          <p className="text-sm text-[#5C6670] p-3 text-center">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-[#5C6670] p-3 text-center">No contacts found</p>
        ) : filtered.map(c => {
          const checked = selectedIds.includes(c._id);
          return (
            <label
              key={c._id}
              className={`flex items-center gap-3 px-3 py-2 hover:bg-[#FBF8F2] cursor-pointer border-b border-[#F3EFE7] last:border-0 ${checked ? 'bg-[#F8EFED]' : ''}`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(c._id)}
                className="h-3.5 w-3.5 rounded accent-[#A62B1F]"
              />
              <Avatar className="h-7 w-7 flex-shrink-0">
                <AvatarFallback className="text-xs bg-[#F7ECEA] text-[#A62B1F]">
                  {(c.full_name || '?').slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#1E2A33] truncate">{c.full_name}</p>
                <p className="text-xs text-[#5C6670] truncate">{c.email} {c.company ? `· ${c.company}` : ''}</p>
              </div>
              {checked && <CheckCircle2 className="w-3.5 h-3.5 text-[#A62B1F] flex-shrink-0" />}
            </label>
          );
        })}
      </div>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
export default function PromoCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [sending, setSending] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const load = async () => {
    try {
      const r = await promoAPI.list();
      setCampaigns(r.data || []);
    } catch {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCampaign = async (campaign) => {
    try {
      const r = await promoAPI.get(campaign.id);
      setSelectedCampaign(r.data);
    } catch {
      toast.error('Failed to load campaign');
    }
  };

  const handleCreate = async (data) => {
    setCreating(true);
    try {
      await promoAPI.create({
        title: data.title,
        track_title: data.track_title,
        artist: data.artist,
        stream_url: data.stream_url,
        wav_url: data.wav_url || data.stream_url,
        artwork_url: data.artwork_url || undefined,
        message: data.message || undefined,
      });
      toast.success('Campaign created');
      reset();
      setShowCreate(false);
      load();
    } catch {
      toast.error('Failed to create campaign');
    } finally {
      setCreating(false);
    }
  };

  const handleSend = async () => {
    if (!selectedCampaign || selectedContactIds.length === 0) return;
    setSending(true);
    try {
      const frontendBase = window.location.origin;
      const r = await promoAPI.send(selectedCampaign.id, {
        contact_ids: selectedContactIds,
        frontend_base_url: frontendBase,
      });
      const { sent, errors: errs } = r.data;
      if (errs?.length) {
        toast.warning(`Sent to ${sent}, ${errs.length} failed`);
      } else {
        toast.success(`Sent to ${sent} recipient${sent !== 1 ? 's' : ''} 🎵`);
      }
      setSelectedContactIds([]);
      const updated = await promoAPI.get(selectedCampaign.id);
      setSelectedCampaign(updated.data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await promoAPI.delete(deleteTarget.id);
      toast.success('Campaign deleted');
      setDeleteTarget(null);
      if (selectedCampaign?.id === deleteTarget.id) setSelectedCampaign(null);
      load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const listenUrl = (token) => `${window.location.origin}/promo/${token}`;

  return (
    <Layout title="Promo Campaigns">
      <div className="space-y-4">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
              Promo Campaigns
            </h1>
            <p className="text-sm text-[#5C6670] mt-0.5">
              Send new releases to DJs and industry contacts · track streams, likes &amp; downloads
            </p>
          </div>
          <Button
            style={{ backgroundColor: '#A62B1F', color: 'white' }}
            onClick={() => setShowCreate(true)}
          >
            <Plus className="w-4 h-4 mr-2" />New Campaign
          </Button>
        </div>

        {/* Campaign list */}
        {loading ? (
          <p className="text-sm text-[#5C6670] py-10 text-center">Loading…</p>
        ) : campaigns.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#A62B1F] to-[#A62B1F] flex items-center justify-center mx-auto mb-4">
                <Music className="w-8 h-8 text-white" />
              </div>
              <p className="text-lg font-bold text-[#1E2A33] mb-1" style={{ fontFamily: 'Manrope' }}>
                No promo campaigns yet
              </p>
              <p className="text-sm text-[#5C6670] mb-4">
                Create your first campaign to send a new release to your contact list.
              </p>
              <Button style={{ backgroundColor: '#A62B1F', color: 'white' }} onClick={() => setShowCreate(true)}>
                <Plus className="w-4 h-4 mr-2" />Create Campaign
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {campaigns.map(c => (
              <CampaignCard
                key={c.id}
                campaign={c}
                onClick={() => openCampaign(c)}
                onDelete={setDeleteTarget}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Create Campaign Sheet ── */}
      <Sheet open={showCreate} onOpenChange={setShowCreate}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-xl font-bold text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
              New Promo Campaign
            </SheetTitle>
            <SheetDescription>Set up a track to send to your DJs and industry contacts</SheetDescription>
          </SheetHeader>
          <form onSubmit={handleSubmit(handleCreate)} className="mt-6 space-y-4">
            <div>
              <Label className="text-sm">Campaign Name *</Label>
              <Input
                {...register('title', { required: true })}
                placeholder="e.g. Summer Drop 2025"
                className="mt-1"
              />
            </div>
            <Separator />
            <p className="text-xs font-semibold text-[#5C6670] uppercase tracking-wide">Track Info</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Track Title *</Label>
                <Input {...register('track_title', { required: true })} placeholder="Track name" className="mt-1" />
              </div>
              <div>
                <Label className="text-sm">Artist *</Label>
                <Input {...register('artist', { required: true })} placeholder="Artist name" className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-sm">Stream URL *</Label>
              <Input
                {...register('stream_url', { required: true })}
                placeholder="https://anyonemusic.com/tracks/your-track.mp3"
                className="mt-1"
              />
              <p className="text-xs text-[#9AA3AB] mt-1">Direct audio URL for streaming (MP3, WAV, or hosted player)</p>
            </div>
            <div>
              <Label className="text-sm">WAV Download URL</Label>
              <Input
                {...register('wav_url')}
                placeholder="https://… (leave blank to use stream URL)"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm">Artwork URL</Label>
              <Input {...register('artwork_url')} placeholder="https://… (optional)" className="mt-1" />
            </div>
            <Separator />
            <div>
              <Label className="text-sm">Personal Message</Label>
              <Textarea
                {...register('message')}
                placeholder="Hey — check out this new track, would love to hear your thoughts…"
                rows={3}
                className="mt-1 text-sm"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={creating} style={{ backgroundColor: '#A62B1F', color: 'white' }} className="flex-1">
                {creating ? 'Creating…' : 'Create Campaign'}
              </Button>
              <Button type="button" variant="outline" onClick={() => { setShowCreate(false); reset(); }}>
                Cancel
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* ── Campaign Detail Sheet ── */}
      {selectedCampaign && (
        <Sheet open={!!selectedCampaign} onOpenChange={() => setSelectedCampaign(null)}>
          <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
            <SheetHeader>
              <div className="flex items-start gap-3">
                {selectedCampaign.artwork_url ? (
                  <img src={selectedCampaign.artwork_url} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                ) : (
                  <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-[#A62B1F] to-[#A62B1F] flex items-center justify-center flex-shrink-0">
                    <Music className="w-6 h-6 text-white" />
                  </div>
                )}
                <div>
                  <SheetTitle className="text-xl font-bold text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
                    {selectedCampaign.track_title}
                  </SheetTitle>
                  <SheetDescription>{selectedCampaign.artist} · {selectedCampaign.title}</SheetDescription>
                </div>
              </div>
            </SheetHeader>

            <div className="mt-6 space-y-5">

              {/* Stats */}
              {selectedCampaign.status === 'sent' && (
                <div className="grid grid-cols-4 gap-3">
                  {[
                    { icon: Users, val: selectedCampaign.stats?.total || 0, label: 'Sent', color: 'bg-[#F7ECEA] text-[#A62B1F]' },
                    { icon: Play,  val: selectedCampaign.stats?.played || 0, label: 'Played', color: 'bg-blue-50 text-blue-600' },
                    { icon: Heart, val: selectedCampaign.stats?.liked || 0, label: 'Liked', color: 'bg-red-50 text-[#A62B1F]' },
                    { icon: Download, val: selectedCampaign.stats?.downloaded || 0, label: 'Downloaded', color: 'bg-purple-50 text-purple-600' },
                  ].map(({ icon: Icon, val, label, color }) => (
                    <Card key={label}>
                      <CardContent className="pt-4 pb-3 text-center">
                        <div className={`w-8 h-8 rounded-full ${color} flex items-center justify-center mx-auto mb-1`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <p className="text-2xl font-bold text-[#1E2A33]">{val}</p>
                        <p className="text-xs text-[#5C6670]">{label}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              <Separator />

              {/* Send to new contacts */}
              <div>
                <p className="font-semibold text-[#1E2A33] text-sm mb-3" style={{ fontFamily: 'Manrope' }}>
                  <Send className="w-4 h-4 inline mr-1.5 text-[#5C6670]" />
                  Send to Contacts
                </p>
                <ContactPicker
                  selectedIds={selectedContactIds}
                  onChange={setSelectedContactIds}
                />
                <Button
                  className="mt-3 w-full"
                  disabled={selectedContactIds.length === 0 || sending}
                  onClick={handleSend}
                  style={{ backgroundColor: '#A62B1F', color: 'white' }}
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sending ? 'Sending…' : `Send to ${selectedContactIds.length} contact${selectedContactIds.length !== 1 ? 's' : ''}`}
                </Button>
              </div>

              {/* Recipients table */}
              {selectedCampaign.recipients?.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <p className="font-semibold text-[#1E2A33] text-sm mb-3" style={{ fontFamily: 'Manrope' }}>
                      <Users className="w-4 h-4 inline mr-1.5 text-[#5C6670]" />
                      Recipients ({selectedCampaign.recipients.length})
                    </p>
                    <div className="space-y-2">
                      {selectedCampaign.recipients.map(r => (
                        <div key={r.id} className="flex items-center gap-3 p-3 border border-[#F3EFE7] rounded-lg bg-[#FBF8F2]">
                          <Avatar className="h-8 w-8 flex-shrink-0">
                            <AvatarFallback className="text-xs bg-[#F7ECEA] text-[#A62B1F]">
                              {(r.contact_name || '?').slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[#1E2A33] truncate">{r.contact_name}</p>
                            <p className="text-xs text-[#5C6670] truncate">{r.contact_email}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span title="Played" className={`w-6 h-6 rounded-full flex items-center justify-center ${r.played ? 'bg-blue-100 text-blue-600' : 'bg-[#F3EFE7] text-[#CFC8BB]'}`}>
                              <Play className="w-3 h-3" />
                            </span>
                            <span title="Liked" className={`w-6 h-6 rounded-full flex items-center justify-center ${r.liked ? 'bg-red-100 text-[#A62B1F]' : 'bg-[#F3EFE7] text-[#CFC8BB]'}`}>
                              <Heart className="w-3 h-3" />
                            </span>
                            <span title="Downloaded" className={`w-6 h-6 rounded-full flex items-center justify-center ${r.downloaded ? 'bg-purple-100 text-purple-600' : 'bg-[#F3EFE7] text-[#CFC8BB]'}`}>
                              <Download className="w-3 h-3" />
                            </span>
                            <a
                              href={listenUrl(r.token)}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Preview listen page"
                              onClick={e => e.stopPropagation()}
                              className="w-6 h-6 rounded-full bg-[#F3EFE7] flex items-center justify-center text-[#5C6670] hover:text-[#A62B1F] hover:bg-[#F7ECEA]"
                            >
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* ── Delete confirm ── */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete "{deleteTarget?.title}" and all recipient records. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
